import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import WebViewer from '@pdftron/webviewer';
import { setupWebViewerPreviewInstance } from '../../utils/webViewerFitWidth';
import { ArrowMaximize20Regular, ArrowMinimize20Regular, Dismiss20Regular } from '@fluentui/react-icons';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { GetAuthenticatedDocUrl } from '../../redux/documentsSlice';
import { isAudioFile } from '../../utils/commonUtils';
import {
    getFileExtensionFromFile,
    getWebViewerIframeOptions,
    isPDFTronSupportedFile,
    supportsPdfCitationHighlight,
} from '../../utils/filePreviewUtils';
import { runCitationHighlightAsync } from './pdfTronCitationHighlight';
import {
    classifyDocumentLoadError,
    fetchDocumentProtectionCheck,
    getProtectedDocumentMessage,
} from '../../utils/protectedDocumentUtils';
import { toast } from 'react-toastify';
import styles from './css/index.module.css';

/**
 * Half-height / side PDFTron embed for AI Warden citation preview.
 */
const AiWardenPdfPanel = ({
    file,
    highlightText,
    pageOneBased,
    onClose,
    expanded = false,
    onToggleExpand,
    headerKicker = 'PDF viewer',
    closeAriaLabel = 'Close citation viewer',
}) => {
    const dispatch = useDispatch();
    const viewerRef = useRef(null);
    const instanceRef = useRef(null);
    const highlightPendingRef = useRef({ text: highlightText || '', page: pageOneBased ?? null });
    const [fileUrl, setFileUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        highlightPendingRef.current = { text: highlightText || '', page: pageOneBased ?? null };
    }, [highlightText, pageOneBased, file?.id]);

    const getExtension = useCallback(() => getFileExtensionFromFile(file), [file]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setError(null);
            setPdfLoading(true);
            setFileUrl(null);
            instanceRef.current = null;
            if (!file?.document) {
                setPdfLoading(false);
                setError('No file URL for this document.');
                return;
            }
            if (!isPDFTronSupportedFile(file)) {
                setPdfLoading(false);
                setError('This file type cannot be previewed here.');
                return;
            }
            const documentUrl = file.document;
            try {
                if (documentUrl.includes('.blob.core.windows.net')) {
                    const authenticatedUrl = await dispatch(GetAuthenticatedDocUrl(documentUrl)).unwrap();
                    if (!cancelled) setFileUrl(authenticatedUrl);
                } else if (!isAudioFile(documentUrl)) {
                    if (!cancelled) setFileUrl(documentUrl);
                } else {
                    if (!cancelled) setError('Audio preview is not supported in this panel.');
                }
            } catch (e) {
                if (!cancelled) {
                    setError('Could not load a secure link for this file.');
                    setPdfLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [file, dispatch]);

    useEffect(() => {
        if (!fileUrl || !viewerRef.current) return undefined;
        if (!isPDFTronSupportedFile(file)) return undefined;

        if (instanceRef.current) {
            instanceRef.current = null;
        }

        setPdfLoading(true);
        const fileExtension = getExtension();
        let disposed = false;

        const blockProtectedPreview = reason => {
            const message = getProtectedDocumentMessage(reason);
            toast.warning(message, {
                position: 'top-right',
                toastId: `protected-ai-warden-${file?.id}`,
            });
            setError(message);
            setPdfLoading(false);
        };

        const initWebViewer = async () => {
            const protection = await fetchDocumentProtectionCheck(fileUrl, fileExtension);
            if (disposed) return;

            if (protection.protected) {
                blockProtectedPreview(protection.reason);
                return;
            }

            WebViewer.Iframe(
                getWebViewerIframeOptions({
                    fileUrl,
                    extension: fileExtension,
                    filename: file?.document_name,
                }),
                viewerRef.current,
            )
                .then(instance => {
                    if (disposed) return;
                    instanceRef.current = instance;

                    setupWebViewerPreviewInstance(instance, {
                        extension: fileExtension,
                        isDisposed: () => disposed,
                        onReady: () => setPdfLoading(false),
                        onDocumentLoadError: err => {
                            console.error('AI Warden PDF panel load error:', err);
                            if (classifyDocumentLoadError(err) === 'protected') {
                                blockProtectedPreview('generic_protected');
                                return;
                            }
                            setError('Failed to open this document in the viewer.');
                        },
                    onDocumentLoaded: () => {
                        if (!supportsPdfCitationHighlight(fileExtension)) return;
                        const pending = highlightPendingRef.current;
                        const text = pending?.text || '';
                        window.setTimeout(() => {
                            if (disposed || !instanceRef.current) return;
                            void runCitationHighlightAsync(instanceRef.current, text, {
                                pageOneBased: pending?.page,
                            }).catch(err => console.warn('Citation highlight:', err));
                        }, 450);
                    },
                    });
                })
                .catch(err => {
                    console.error('PDFTron init (AI Warden):', err);
                    if (!disposed) {
                        setPdfLoading(false);
                        setError('Viewer failed to start.');
                    }
                });
        };

        initWebViewer();

        return () => {
            disposed = true;
            instanceRef.current = null;
        };
    }, [fileUrl, file, getExtension]);

    useEffect(() => {
        if (!instanceRef.current?.UI?.resize) return undefined;
        const timer = window.setTimeout(() => {
            try {
                instanceRef.current?.UI?.resize();
            } catch (err) {
                console.warn('PDF viewer resize:', err);
            }
        }, 200);
        return () => window.clearTimeout(timer);
    }, [expanded]);

    useEffect(() => {
        if (!expanded) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKeyDown = event => {
            if (event.key === 'Escape') onToggleExpand?.();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [expanded, onToggleExpand]);

    return (
        <div className={`${styles.aiWardenPdfColumn} ${expanded ? styles.aiWardenPdfFullscreen : ''}`}>
            <div className={styles.aiWardenPdfHeader}>
                <div className={styles.aiWardenPdfTitleWrap}>
                    <span className={styles.aiWardenKicker}>{headerKicker}</span>
                    <span className={styles.aiWardenPdfTitle} title={file?.document_name || file?.name}>
                        {file?.document_name || file?.name || `File ${file?.id}`}
                    </span>
                </div>
                <div className={styles.aiWardenPdfHeaderActions}>
                    {onToggleExpand ? (
                        <button
                            type="button"
                            className={styles.aiWardenPdfClose}
                            onClick={onToggleExpand}
                            aria-label={expanded ? 'Exit full screen' : 'Open full screen'}>
                            {expanded ? <ArrowMinimize20Regular /> : <ArrowMaximize20Regular />}
                        </button>
                    ) : null}
                    <button type="button" className={styles.aiWardenPdfClose} onClick={onClose} aria-label={closeAriaLabel}>
                        <Dismiss20Regular />
                    </button>
                </div>
            </div>
            <div className={`${styles.aiWardenPdfBody} ${expanded ? styles.aiWardenPdfBodyFullscreen : ''}`}>
                {error && <p className={styles.aiWardenPdfError}>{error}</p>}
                {!error && (
                    <div key={file?.id || 'wv'} className={`webviewer ${styles.aiWardenPdfViewport}`} ref={viewerRef}>
                        {pdfLoading && (
                            <div className={styles.aiWardenPdfLoading}>
                                <AiOutlineLoading3Quarters className="rotateLoader" size={36} />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AiWardenPdfPanel;
